import type { ReactElement } from 'react';
import React, { useState } from 'react';
import axios from 'axios';
import { isEmpty } from 'lodash';

import { getClaimNotificationIcon } from '~/components/ClaimNotificationsCard/ClaimNotificationCardUtils';
import type { NotificationTypesUnion, UpdateNotification } from '~/components/ClaimNotificationsCard/types';
import NotificationAssignee from '~/components/Notifications/Assignee';
import CategoryEditableChip from '~/components/Notifications/CategoryEditableChip';
import DismissButton from '~/components/Notifications/DismissButton';
import MarkAsUnreadButton from '~/components/Notifications/MarkAsUnreadButton';
import NotificationDueDate from '~/components/Notifications/NotificationDueDate';
import PriorityEditableChip from '~/components/Notifications/PriorityEditableChip';
import { changeClaimNotificationDismissState } from '~/components/Notifications/utils';
import useOrganization from '~/components/OrganizationContext';
import type { CategoryModel } from '~/components/SystemConfiguration/NotificationsConfiguration/NotificationsCategories';
import Divider from '~/components/ui/atoms/Divider';
import { reportAxiosError } from '~/Utils';

import Caption from '../core/TextComponents/Caption';

export interface GenericNotificationHeaderProps {
  notification: NotificationTypesUnion;
  onClose: () => void;
  onMarkAsUnread: (notificationId: number) => void;
  onUpdateNotification: UpdateNotification;
}
const GenericNotificationHeader: React.FC<GenericNotificationHeaderProps> = ({
  notification,
  onClose,
  onMarkAsUnread,
  onUpdateNotification,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { notificationCategories } = useOrganization() as {
    notificationCategories: CategoryModel[];
  };

  const handleMarkAsUnread = async () => {
    try {
      setIsUpdating(true);
      await axios.post(`/api/v1/claims/notifications/${notification.id}/mark_as_unread`);
      onMarkAsUnread(notification.id);
      onClose();
    } catch (error) {
      reportAxiosError(error);
    }
    setIsUpdating(false);
  };

  return (
    <div>
      <div className="mb-16 border-1 border-solid border-slate-600 bg-slate-200 pb-12 shadow-sm">
        <div className="m-12 p-12">
          <div className="grid grid-cols-[repeat(3,auto)_1fr_auto] gap-12">
            <ComponentWithLabel label="Type" ariaLabel="notification-icon">
              {getClaimNotificationIcon(notification, undefined, 20)}
            </ComponentWithLabel>
            <ComponentWithLabel label="Priority">
              <PriorityEditableChip
                notification={notification}
                setIsUpdating={setIsUpdating}
                onUpdate={onUpdateNotification}
                disabled={isUpdating}
              />
            </ComponentWithLabel>
            <ComponentWithLabel label="Due Date">
              <NotificationDueDate
                notification={notification}
                disabled={isUpdating}
                setIsUpdating={setIsUpdating}
                onUpdate={onUpdateNotification}
              />
            </ComponentWithLabel>
            <ComponentWithLabel label="Assignee">
              <NotificationAssignee notification={notification} />
            </ComponentWithLabel>
            {!isEmpty(notificationCategories) ? (
              <ComponentWithLabel label="Category">
                <CategoryEditableChip
                  claimNotification={notification}
                  disabled={isUpdating}
                  setIsUpdating={setIsUpdating}
                  onUpdate={onUpdateNotification}
                />
              </ComponentWithLabel>
            ) : null}
          </div>
        </div>
        <Divider className="mb-16 mt-16 w-full" />

        <div className="m-8 flex items-center justify-between">
          <DismissButton
            notification={notification}
            onClick={async () => {
              await changeClaimNotificationDismissState(
                notification,
                () => setIsUpdating(true),
                () => setIsUpdating(false),
                (id, state) => onUpdateNotification(id, { datetime_dismissed: state })
              );
              onClose();
            }}
            tooltipTextDismiss="Dismiss and close"
            tooltipTextUndismiss="Undismiss and close"
            disabled={isUpdating}
            textDismiss="DISMISS AND CLOSE"
            textUndismiss="UNDISMISS AND CLOSE"
          />
          <MarkAsUnreadButton onClick={handleMarkAsUnread} disabled={isUpdating} />
        </div>
      </div>
    </div>
  );
};

const ComponentWithLabel: React.FC<{ label: string; children: ReactElement; ariaLabel?: string }> = ({
  label,
  children,
  ariaLabel,
}) => (
  <div aria-label={ariaLabel}>
    <Caption variant={Caption.VARIANTS.LABEL} className="mb-4">
      {label}
    </Caption>
    {children}
  </div>
);

export default GenericNotificationHeader;
